'use client';
import { useState, useRef, useEffect } from 'react';
import Image from 'next/image';
import styles from './ProductGallery.module.css';

export default function ProductGallery({ images }) {
  const [mainImage, setMainImage] = useState(0);
  const imageRef = useRef(null);
  const loupeRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    const container = imageRef.current;
    const loupe = loupeRef.current;
    if (!container || !loupe) return;

    const handleMouseMove = (e) => {
      // Cancel previous frame
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }

      // Use RAF for smooth 60fps updates
      rafRef.current = requestAnimationFrame(() => {
        const rect = container.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        // Use CSS transforms (GPU accelerated)
        loupe.style.transform = `translate(${x - 75}px, ${y - 75}px)`;
        loupe.style.backgroundPosition = `-${x * 2 - 75}px -${y * 2 - 75}px`;
      });
    };

    const handleMouseEnter = () => {
      loupe.style.opacity = '1';
      loupe.style.visibility = 'visible';
    };

    const handleMouseLeave = () => {
      loupe.style.opacity = '0';
      loupe.style.visibility = 'hidden';
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };

    container.addEventListener('mousemove', handleMouseMove, { passive: true });
    container.addEventListener('mouseenter', handleMouseEnter);
    container.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      container.removeEventListener('mousemove', handleMouseMove);
      container.removeEventListener('mouseenter', handleMouseEnter);
      container.removeEventListener('mouseleave', handleMouseLeave);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [mainImage]);

  return (
    <div className={styles.gallery}>
      <div
        className={styles.mainImageContainer}
        ref={imageRef}
      >
        <Image
          src={images[mainImage]}
          alt="Product"
          className={styles.mainImage}
          width={800}
          height={500}
          priority
          unoptimized
        />

        <div
          ref={loupeRef}
          className={styles.loupe}
          style={{
            backgroundImage: `url(${images[mainImage]})`,
            backgroundSize: `${800 * 2}px ${500 * 2}px`,
            opacity: 0,
            visibility: 'hidden',
            willChange: 'transform'
          }}
        />
      </div>

      <div className={styles.thumbnails}>
        {images.map((img, idx) => (
          <div
            key={idx}
            className={`${styles.thumbnail} ${mainImage === idx ? styles.active : ''}`}
            onClick={() => setMainImage(idx)}
          >
            <Image
              src={img}
              alt={`View ${idx + 1}`}
              width={200}
              height={125}
              unoptimized
            />
          </div>
        ))}
      </div>
    </div>
  );
}
